'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { getBookingUrl } from '@/lib/booking';
import { useLocale } from '@/context/LocaleContext';
import { getPublicStrings } from '@/i18n/public';

export default function ServicesSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const locale = useLocale();
  const copy = getPublicStrings(locale).home.services;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const services = copy.cards;

  return (
    <section
      id="services"
      ref={sectionRef}
      className="py-16 md:py-32 px-6 md:px-12 bg-background border-b border-border"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div
            className={`mb-6 transition-all duration-800 ${
              isVisible ? 'fade-in-up' : 'fade-in-up-init'
            }`}
          >
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold font-black">
              {copy.eyebrow}
            </span>
            <div className="w-12 h-[2px] bg-gold mt-2 mx-auto" />
          </div>
          <h2
            className={`text-4xl md:text-6xl text-foreground leading-tight font-heading font-bold mb-6 transition-all duration-800 delay-100 ${
              isVisible ? 'fade-in-up' : 'fade-in-up-init'
            }`}
          >
            {copy.title} <span className="text-gold italic">{copy.titleItalic}</span>
          </h2>
          <p
            className={`text-lg text-foreground/70 font-light transition-all duration-800 delay-200 ${
              isVisible ? 'fade-in-up' : 'fade-in-up-init'
            }`}
          >
            {copy.intro}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const bookingHref = getBookingUrl(service.id);
            const bookingExternal = bookingHref.startsWith('http');
            return (
              <div
                key={service.id}
                className={`group bg-white p-8 border-t-4 border-gold/30 hover:border-gold hover:shadow-xl hover:shadow-primary/5 hover:scale-[1.02] transition-all duration-300 ${
                  isVisible ? 'fade-in-up' : 'fade-in-up-init'
                }`}
                style={{ animationDelay: `${300 + index * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary transition-colors">
                  <Icon
                    name={service.icon}
                    variant="solid"
                    size={24}
                    className="text-primary group-hover:text-white transition-colors"
                  />
                </div>

                <h3 className="text-xl font-heading font-bold text-primary mb-3">
                  {service.title}
                </h3>
                <p className="text-sm text-foreground/70 leading-relaxed mb-6">
                  {service.description}
                </p>

                <a
                  href={bookingHref}
                  {...(bookingExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-primary group-hover:text-gold transition-colors"
                >
                  {service.ctaLabel}
                  <Icon
                    name="ArrowRightIcon"
                    size={14}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
